import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, switchMap, concatMap, toArray, of, map } from 'rxjs';
import { ObservationReferenceRangeMatcherService } from './observation-reference-range-matcher.service';
import { Observation, Bundle, BundleEntry } from 'fhir/r4';

export const observationReferenceRangeInterceptor: HttpInterceptorFn = (req, next) => {
  const matcherService = inject(ObservationReferenceRangeMatcherService);

  if (req.method === 'POST' || req.method === 'PUT') {
    const body = req.body as any;

    // Handle single Observation
    if (req.url.includes('/Observation') && body && body.resourceType === 'Observation' && body.status === 'final') {
      const observation = body as Observation;
      if (!observation.referenceRange?.length) {
        return matcherService.confirmRangeBeforeSave(observation).pipe(
          switchMap(updatedObs => next(req.clone({ body: updatedObs })))
        );
      }
    }

    // Handle Bundle transactions/batches containing Observations
    if (body && body.resourceType === 'Bundle' && Array.isArray(body.entry)) {
      const bundle = body as Bundle;
      
      // We use from() + concatMap() to sequentially process entries.
      // If an entry is an Observation without a range, it prompts the user (waiting for dialog close).
      // Then it moves to the next entry.
      return from(bundle.entry as BundleEntry[]).pipe(
        concatMap((entry) => {
          const res = entry.resource;
          if (res && res.resourceType === 'Observation' && res.status === 'final') {
            const obs = res as Observation;
            if (!obs.referenceRange?.length) {
              return matcherService.confirmRangeBeforeSave(obs).pipe(
                map(updatedObs => {
                  return { ...entry, resource: updatedObs };
                })
              );
            }
          }
          // If not an observation or already has a range, pass it through unchanged
          return of(entry);
        }),
        toArray(),
        switchMap((updatedEntries: BundleEntry[]) => {
          const newBundle = { ...bundle, entry: updatedEntries };
          return next(req.clone({ body: newBundle }));
        })
      );
    }
  }

  // Not an Observation POST/PUT, pass through normally
  return next(req);
};
